/**
 * Chat API service for SSE streaming communication with the Chat Lambda Function URL.
 * Handles message sending, SSE stream parsing, and error handling.
 */

import type { SSEEvent, ChatAttachment, Activity } from '@/types/chat';

/** Chat Lambda Function URL from environment */
const CHAT_URL = import.meta.env.VITE_CHAT_URL;

/**
 * Authentication error thrown when the Chat API returns 401.
 */
export class AuthError extends Error {
  constructor(message: string = 'Session expired') {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * API error thrown for non-401 HTTP errors from the Chat API.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Tool call event data passed to the onToolCall callback */
export interface ToolCallEvent {
  name: string;
  result: Record<string, unknown>;
}

/** Agent types available for routing */
export type AgentType = 'analyst' | 'clearing';

/** Request body sent to the Chat API */
export interface ChatRequest {
  message: string;
  session_id: string;
  agent: AgentType;
  account_id?: string;
  attachments?: Pick<ChatAttachment, 'name' | 'mimeType' | 'base64'>[];
}

/**
 * Parse a single SSE data line into a typed event object.
 * Returns null for empty or unparseable lines.
 */
function parseSSEEvent(line: string): SSEEvent | null {
  const prefix = 'data: ';
  if (!line.startsWith(prefix)) {
    return null;
  }

  const jsonStr = line.slice(prefix.length).trim();
  if (!jsonStr) {
    return null;
  }

  try {
    return JSON.parse(jsonStr) as SSEEvent;
  } catch {
    return null;
  }
}

/**
 * Build an Activity from a parsed 'activity' SSE event.
 *
 * Requires all fields the Activity_Event schema mandates (activity_id,
 * activity_type, status, label, ts). Returns null — meaning "drop this
 * activity" — when any required field is missing, so a malformed activity is
 * silently discarded and stream processing continues (Requirements 2.6, 4.5).
 *
 * The activity_type is surfaced as-is, including values the UI does not
 * recognize, so that unknown/reserved types render generically rather than
 * being filtered out (Requirements 4.3, 10.3).
 */
function toActivity(event: SSEEvent): Activity | null {
  const { activity_id, activity_type, status, label, ts, detail } = event;
  if (!activity_id || !activity_type || !status || !label || !ts) {
    return null;
  }
  return {
    id: activity_id,
    type: activity_type,
    status,
    label,
    ts,
    detail,
  };
}

/**
 * Send a chat message and process the SSE-streamed response.
 *
 * @param request - The chat request containing message, session_id, and agent
 * @param token - Cognito JWT token for Authorization header
 * @param onChunk - Callback invoked with each text chunk as it arrives
 * @param onToolCall - Callback invoked when a tool call event is received
 * @param onComplete - Callback invoked when the stream completes (done event)
 * @param onError - Callback invoked when an error event is received in the stream
 * @param signal - Optional AbortSignal for request cancellation
 * @param onWarning - Optional callback invoked when a warning event is received
 * @param onActivity - Optional callback invoked when a valid activity event is received
 */
export async function sendChatMessage(
  request: ChatRequest,
  token: string,
  onChunk: (text: string) => void,
  onToolCall: (toolCall: ToolCallEvent) => void,
  onComplete: () => void,
  onError: (error: Error) => void,
  signal?: AbortSignal,
  onWarning?: (message: string) => void,
  onActivity?: (activity: Activity) => void
): Promise<void> {
  const response = await fetch(CHAT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthError('Session expired');
    }
    const errorText = await response.text();
    throw new ApiError(errorText || `HTTP ${response.status}`, response.status);
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        // Process any remaining buffered data
        if (buffer.trim()) {
          processBufferedLines(buffer, onChunk, onToolCall, onComplete, onError, onWarning, onActivity);
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by double newlines
      const parts = buffer.split('\n\n');
      // Keep the last incomplete part in the buffer
      buffer = parts.pop() || '';

      for (const part of parts) {
        const lines = part.split('\n');
        for (const line of lines) {
          const event = parseSSEEvent(line);
          if (!event) continue;

          switch (event.type) {
            case 'text':
              if (event.content) {
                onChunk(event.content);
              }
              break;

            case 'tool_call':
              if (event.name && event.result) {
                onToolCall({ name: event.name, result: event.result });
              }
              break;

            case 'trade_confirmation':
              if (event.trade) {
                onToolCall({
                  name: 'trade_confirmation',
                  result: event.trade as unknown as Record<string, unknown>,
                });
              }
              break;

            case 'trade_result':
              onToolCall({
                name: 'trade_result',
                result: { wash_sale: event.wash_sale ?? false },
              });
              break;

            case 'done':
              onComplete();
              return;

            case 'error':
              onError(new Error(event.message || 'Unknown streaming error'));
              return;

            case 'warning':
              if (event.message) {
                onWarning?.(event.message);
              }
              break;

            case 'activity': {
              // Additive and isolated: validate, surface valid activities, and
              // drop malformed ones. Must never throw, never touch the text
              // path, and never short-circuit the loop (Requirements 4.3, 4.4,
              // 4.5, 10.3).
              const activity = toActivity(event);
              if (activity) {
                onActivity?.(activity);
              }
              break;
            }
          }
        }
      }
    }
  } catch (error) {
    // AbortError is expected when the user cancels — re-throw without wrapping
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    throw error;
  } finally {
    reader.releaseLock();
  }
}

/**
 * Process any remaining buffered lines when the stream ends.
 */
function processBufferedLines(
  buffer: string,
  onChunk: (text: string) => void,
  onToolCall: (toolCall: ToolCallEvent) => void,
  onComplete: () => void,
  onError: (error: Error) => void,
  onWarning?: (message: string) => void,
  onActivity?: (activity: Activity) => void
): void {
  const lines = buffer.split('\n');
  for (const line of lines) {
    const event = parseSSEEvent(line);
    if (!event) continue;

    switch (event.type) {
      case 'text':
        if (event.content) {
          onChunk(event.content);
        }
        break;
      case 'tool_call':
        if (event.name && event.result) {
          onToolCall({ name: event.name, result: event.result });
        }
        break;
      case 'trade_confirmation':
        if (event.trade) {
          onToolCall({
            name: 'trade_confirmation',
            result: event.trade as unknown as Record<string, unknown>,
          });
        }
        break;
      case 'trade_result':
        onToolCall({
          name: 'trade_result',
          result: { wash_sale: event.wash_sale ?? false },
        });
        break;
      case 'done':
        onComplete();
        return;
      case 'error':
        onError(new Error(event.message || 'Unknown streaming error'));
        return;
      case 'warning':
        if (event.message) {
          onWarning?.(event.message);
        }
        break;
      case 'activity': {
        const activity = toActivity(event);
        if (activity) {
          onActivity?.(activity);
        }
        break;
      }
    }
  }
}
